import CheckIcon from '@mui/icons-material/CheckCircle'
import SendIcon from '@mui/icons-material/Send'
import { Box, Button, Dialog, DialogActions, DialogContent, TextField, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useNotify, useTranslate } from 'ra-core'
import { useState } from 'react'

import SalesEmailFallback from './SalesEmailFallback'
import SalesRequestBenefit from './SalesRequestBenefit'
import { useApi } from '../api/apiProvider'

const MESSAGE_MAX_LENGTH = 2000

const SalesRequestDialog = ({
  open,
  onClose,
  subject,
  title,
  subtitle,
  benefits = [],
  highlights = [],
  messagePlaceholder,
  submitLabel,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [message, setMessage] = useState('')
  const [hasFailed, setHasFailed] = useState(false)

  const close = () => {
    setMessage('')
    setHasFailed(false)
    onClose()
  }

  const [sendRequest, { isLoading }] = useApi('/sales-requests', {
    method: 'POST',
    onSuccess: () => {
      notify('mymove.salesRequest.sent', { type: 'success' })
      close()
    },
    onFailure: () => setHasFailed(true),
  })

  const submit = () => sendRequest({ body: JSON.stringify({ subject, message: message.trim() }) })

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogContent sx={{ p: { xs: 2, sm: 4 } }}>
        <Box
          sx={(theme) => ({
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            border: '2px solid',
            borderColor: 'primary.main',
            bgcolor: alpha(theme.palette.primary.main, 0.06),
          })}
        >
          <Typography variant="h5" fontWeight="bold" fontSize={{ xs: '1.25rem', sm: '1.5rem' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2.5}>
          {benefits.map((benefit) => (
            <SalesRequestBenefit key={benefit.title} {...benefit} />
          ))}
        </Box>
        {highlights.length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={2} mt={3}>
            {highlights.map((highlight) => (
              <Box key={highlight} display="flex" alignItems="center" gap={0.5}>
                <CheckIcon fontSize="small" color="success" />
                <Typography variant="body2" fontWeight="medium">
                  {highlight}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        <TextField
          placeholder={messagePlaceholder}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          multiline
          minRows={3}
          helperText={`${message.length} / ${MESSAGE_MAX_LENGTH}`}
          slotProps={{
            htmlInput: { maxLength: MESSAGE_MAX_LENGTH },
            formHelperText: { sx: { textAlign: 'right', mx: 0 } },
          }}
          sx={{ mt: 3, mx: 0, width: '100%' }}
        />
        {hasFailed && <SalesEmailFallback />}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 4 }, pb: { xs: 2, sm: 3 } }}>
        <Button onClick={close}>{translate('ra.action.close')}</Button>
        <Button
          variant="contained"
          size="large"
          startIcon={<SendIcon />}
          onClick={submit}
          disabled={isLoading}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SalesRequestDialog
